import { ConfirmModal } from "common/ConfirmModal"
import { Modal } from "common/Modal"
import { Table } from "common/Table/Table"
import { Disclosure } from "@headlessui/react"
import { Checkbox, Radio } from "flowbite-react"
import { useNotifications } from "hooks/useNotification"
import { useThunkDispatch } from "hooks/useThunkDispatch"
import {
  ChangeEvent,
  Fragment,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useSelector } from "react-redux"
import { setUser } from "appRedux/reducers/userReducer"
import { userSelector } from "appRedux/selectors/user"
import UserService from "services/user.service"
import { exportToCSV } from "utils/funcs"
import {
  permissionGroups,
  PermissionAction,
  PermissionActionConfig,
  PermissionModuleConfig,
} from "utils/permissionConfig"
import {
  createPermissionKey,
  hasPermission,
  hydratePermissions,
  serializePermissions,
} from "utils/permissions"

const service = new UserService()

const initialFormState = {
  fullname: "",
  email: "",
  password: "",
  role: "",
}

const strongPasswordRegExp = new RegExp(
  "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})"
)

const emailRegExp = new RegExp(
  "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
)

const roleLabels: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  operator: "Operator",
  user: "User",
}

const roleHierarchy: Record<string, string[]> = {
  super_admin: ["admin", "operator", "user"],
  admin: ["operator", "user"],
  operator: [],
  user: [],
}

const roleWeights: Record<string, number> = {
  super_admin: 3,
  admin: 2,
  operator: 1,
  user: 0,
}

const normalizeRole = (role?: string | null) => role ?? "user"

const getRoleWeight = (role: string) => roleWeights[role] ?? -1

const canActOnRole = (
  actorRole: string,
  targetRole: string,
  { allowSelf = false }: { allowSelf?: boolean } = {}
) => {
  if (allowSelf && actorRole === targetRole) {
    return true
  }
  return getRoleWeight(actorRole) > getRoleWeight(targetRole)
}

const getAvailableRoleOptions = (role: string) => roleHierarchy[role] ?? []

const buildPermissionKey = (moduleKey: string, action: PermissionAction) =>
  createPermissionKey(moduleKey, action)

export const Users = () => {
  const [userToEdit, setUserToEdit] = useState<any | null>(null)
  const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null)
  const [permissionModal, setPermissionModal] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [permissionSelection, setPermissionSelection] = useState<string[]>([])
  const [data, setData] = useState<any[]>([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formState, setFormState] = useState(initialFormState)

  const user = useSelector(userSelector)
  const dispatch = useThunkDispatch()

  const { successMessage, errorMessage } = useNotifications()

  const currentRole = normalizeRole(user?.admin)
  const currentUserId = user?._id

  const roleOptions = useMemo(
    () =>
      getAvailableRoleOptions(currentRole).map((value) => ({
        value,
        label: roleLabels[value] ?? value,
      })),
    [currentRole]
  )

  const canCreateAccounts =
    currentRole === "super_admin" ||
    currentRole === "admin" ||
    hasPermission(user, "accounts", "create")

  const canEditAccounts =
    currentRole === "super_admin" ||
    currentRole === "admin" ||
    hasPermission(user, "accounts", "edit")

  const canDeleteAccounts =
    currentRole === "super_admin" ||
    currentRole === "admin" ||
    hasPermission(user, "accounts", "delete")

  const selectionSet = useMemo(
    () => new Set(permissionSelection),
    [permissionSelection]
  )

  const isActionSelected = useCallback(
    (moduleKey: string, action: PermissionAction) =>
      selectionSet.has(buildPermissionKey(moduleKey, action)),
    [selectionSet]
  )

  const moduleIsFullySelected = useCallback(
    (module: PermissionModuleConfig) =>
      module.actions.every((action) =>
        isActionSelected(module.key, action.action)
      ),
    [isActionSelected]
  )

  const togglePermission = useCallback(
    (moduleKey: string, action: PermissionAction) => {
      setPermissionSelection((current) => {
        const next = new Set(current)
        const key = buildPermissionKey(moduleKey, action)

        if (action === "view") {
          if (next.has(key)) {
            Array.from(next)
              .filter((value) => value.startsWith(`${moduleKey}:`))
              .forEach((value) => next.delete(value))
          } else {
            next.add(key)
          }
        } else {
          if (next.has(key)) {
            next.delete(key)
            const hasAdditionalActions = Array.from(next).some(
              (value) =>
                value.startsWith(`${moduleKey}:`) &&
                !value.endsWith(":view")
            )
            if (!hasAdditionalActions) {
              next.delete(buildPermissionKey(moduleKey, "view"))
            }
          } else {
            next.add(key)
            next.add(buildPermissionKey(moduleKey, "view"))
          }
        }

        return Array.from(next)
      })
    },
    []
  )

  const toggleModulePermissions = useCallback(
    (module: PermissionModuleConfig, enable: boolean) => {
      setPermissionSelection((current) => {
        const next = new Set(current)
        if (enable) {
          module.actions.forEach((action) => {
            next.add(buildPermissionKey(module.key, action.action))
            if (action.action !== "view") {
              next.add(buildPermissionKey(module.key, "view"))
            }
          })
        } else {
          Array.from(next)
            .filter((value) => value.startsWith(`${module.key}:`))
            .forEach((value) => next.delete(value))
        }
        return Array.from(next)
      })
    },
    []
  )

  const renderActionLabel = (
    module: PermissionModuleConfig,
    action: PermissionActionConfig
  ) => (
    <label
      key={`${module.key}-${action.action}`}
      className="flex items-center gap-2 text-sm text-gray-700"
      htmlFor={`${module.key}-${action.action}`}
    >
      <Checkbox
        id={`${module.key}-${action.action}`}
        name={`${module.key}-${action.action}`}
        checked={isActionSelected(module.key, action.action)}
        onChange={() => togglePermission(module.key, action.action)}
      />
      <span>{action.label}</span>
    </label>
  )

  const renderModuleCard = (module: PermissionModuleConfig) => {
    const fullySelected = moduleIsFullySelected(module)
    const partiallySelected =
      !fullySelected &&
      module.actions.some((action) =>
        isActionSelected(module.key, action.action)
      )

    const hasManyActions = module.actions.length > 3
    const gridColumns = hasManyActions
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : "sm:grid-cols-2 lg:grid-cols-3"

    return (
      <div
        key={module.key}
        className="flex flex-col gap-3 p-4 transition-shadow duration-150 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {module.label}
            </p>
            {module.description && (
              <p className="text-xs text-gray-500">{module.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => toggleModulePermissions(module, !fullySelected)}
            className="text-xs font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {fullySelected ? "Clear" : partiallySelected ? "Complete" : "Select all"}
          </button>
        </div>
        <div className={`grid grid-cols-1 gap-2 ${gridColumns}`}>
          {module.actions.map((action) => renderActionLabel(module, action))}
        </div>
      </div>
    )
  }

  const renderPermissionGrid = () => (
    <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">
      {permissionGroups.map((group, index) => (
        <Disclosure key={group.key} defaultOpen={index === 0}>
          {({ open }) => (
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <Disclosure.Button className="flex w-full items-center justify-between gap-4 rounded-lg px-4 py-3 text-left transition hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-primary/40">
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-900">
                    {group.label}
                  </span>
                  {group.description && (
                    <span className="text-xs text-gray-500">
                      {group.description}
                    </span>
                  )}
                </div>
                <svg
                  className={`h-4 w-4 transform text-gray-500 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </Disclosure.Button>

              <Disclosure.Panel className="border-t border-gray-100 px-4 py-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {group.modules.map((module) => renderModuleCard(module))}
                </div>
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  )

  const fetchData = useCallback(() => {
    setLoading(true)
    service
      .getUsers()
      .then((resp) => {
        const incoming = resp?.data?.data ?? []
        const filtered = currentUserId
          ? incoming.filter((record) => record?._id !== currentUserId)
          : incoming
        setData(filtered)
      })
      .catch((err) => {
        console.error("Failed to fetch users", err)
        errorMessage("Unable to load users right now")
      })
      .finally(() => {
        setLoading(false)
      })
  }, [currentUserId, errorMessage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (showModal) {
      setFormState({
        ...initialFormState,
        role: roleOptions[0]?.value ?? "",
      })
      setPermissionSelection([])
      setError("")
    } else {
      setFormState(initialFormState)
      setPermissionSelection([])
    }
  }, [showModal, roleOptions])

  useEffect(() => {
    if (!permissionModal) {
      setUserToEdit(null)
      setPermissionSelection([])
    }
  }, [permissionModal])

  const handleDeleteUser = () => {
    if (!userIdToDelete) return
    service
      .deleteUser(userIdToDelete)
      .then(() => {
        fetchData()
        successMessage("User deleted successfully")
      })
      .catch((err) => {
        console.error("Failed to delete user", err)
        errorMessage("Failed to delete user")
      })
      .finally(() => {
        setConfirm(false)
        setUserIdToDelete(null)
      })
  }

  const handleInputChange =
    (key: keyof typeof initialFormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({
        ...prev,
        [key]: event.target.value,
      }))
    }

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault()
    setError("")

    const { fullname, email, password, role } = formState
    if (!fullname || !email || !password || !role) {
      setError("All fields are required")
      return
    }

    if (!emailRegExp.test(email)) {
      setError("Please enter a valid email address")
      return
    }

    if (!strongPasswordRegExp.test(password)) {
      setError(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
      )
      return
    }

    if (!roleOptions.some((option) => option.value === role)) {
      setError("You are not allowed to assign the selected role")
      return
    }

    setSaving(true)
    const permissions = serializePermissions(permissionSelection)
    const payload = {
      fullname: fullname.trim(),
      email: email.trim(),
      password,
      role,
      admin: role,
      permissions,
    }

    service
      .addUser(payload)
      .then(() => {
        successMessage("User added successfully")
        fetchData()
        setShowModal(false)
      })
      .catch((err) => {
        console.error("Add user failed", err)
        errorMessage("Failed to add user")
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const openPermissionModal = useCallback(
    (record: any) => {
      setUserToEdit(record)
      setPermissionSelection(hydratePermissions(record?.permissions))
      setPermissionModal(true)
      setError("")
    },
    []
  )

  const canEditUserRecord = useCallback(
    (record: any) => {
      const targetRole = normalizeRole(record?.admin)
      if (record?._id === currentUserId) {
        return false
      }
      if (!canEditAccounts) return false
      return canActOnRole(currentRole, targetRole)
    },
    [canEditAccounts, currentRole, currentUserId]
  )

  const canDeleteUserRecord = useCallback(
    (record: any) => {
      if (!canDeleteAccounts) return false
      if (!record?._id || record._id === currentUserId) return false
      const targetRole = normalizeRole(record?.admin)
      return canActOnRole(currentRole, targetRole)
    },
    [canDeleteAccounts, currentRole, currentUserId]
  )

  const updatePermissions = (event: SyntheticEvent) => {
    event.preventDefault()
    if (!userToEdit?._id) return

    service
      .updatePermissions({
        permissions: serializePermissions(permissionSelection),
        userid: userToEdit._id,
      })
      .then((resp) => {
        successMessage("Permissions updated successfully")
        if (user?._id === userToEdit._id && resp?.data?.data) {
          const updatedUser = {
            ...resp.data.data,
            permissions: hydratePermissions(resp.data.data.permissions),
          }
          dispatch(setUser(updatedUser))
        }
        fetchData()
        setPermissionModal(false)
      })
      .catch((err) => {
        console.error("Permission update failed", err)
        errorMessage("Permission update failed")
        setPermissionModal(false)
      })
  }

  const handleExport = () => {
    exportToCSV(data, "users")
  }

  const columns = useMemo(
    () => [
      {
        key: "fullname",
        title: "Full name",
        label: "fullname",
        dbColName: "fullname",
      },
      {
        key: "email",
        title: "Email",
        label: "email",
        dbColName: "email",
      },
      {
        key: "role",
        title: "Role",
        dbColName: "admin",
        render: (value: string) => (
          <span className="text-sm font-medium text-gray-700 capitalize">
            {roleLabels[normalizeRole(value)] ?? roleLabels.user}
          </span>
        ),
      },
      {
        key: "createdAt",
        title: "Created",
        label: "date",
        dbColName: "date",
        render: (date: string) => (
          <p className="text-sm text-gray-600">
            {date ? new Date(date).toLocaleDateString() : "--"}
          </p>
        ),
      },
      {
        key: "actions",
        title: "Actions",
        render: (record: any) => {
          const canEdit = canEditUserRecord(record)
          const canDelete = canDeleteUserRecord(record)

          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                title={
                  canEdit
                    ? "Edit permissions"
                    : "You do not have permission to edit this user"
                }
                onClick={() => openPermissionModal(record)}
                disabled={!canEdit}
                className={`rounded p-1 transition ${
                  canEdit
                    ? "text-blue-600 hover:bg-blue-50"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
                  />
                </svg>
              </button>
              <button
                type="button"
                title={
                  canDelete
                    ? "Delete user"
                    : "You do not have permission to delete this user"
                }
                onClick={() => {
                  setConfirm(true)
                  setUserIdToDelete(record?._id)
                }}
                disabled={!canDelete}
                className={`rounded p-1 transition ${
                  canDelete
                    ? "text-red-600 hover:bg-red-50"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
              </button>
            </div>
          )
        },
      },
    ],
    [canDeleteUserRecord, canEditUserRecord, openPermissionModal]
  )

  const canShowAddButton = canCreateAccounts && roleOptions.length > 0

  return (
    <Fragment>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold tracking-wide text-gray-800">
            User Accounts
          </h1>
          <p className="page-subtitle">Manage dashboard access</p>
        </div>
        {canShowAddButton && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white transition bg-ink hover:bg-gray-800 rounded-lg shadow hover:bg-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v12m6-6H6"
              />
            </svg>
            Add User
          </button>
        )}
      </div>

      <ConfirmModal
        open={confirm}
        setOpen={setConfirm}
        message="Are you sure you want to delete this user?"
        onClickConfirm={handleDeleteUser}
      />

      <Table
        data={data}
        loadingData={loading}
        exportButton={false}
        handleExport={handleExport}
        totalItems={data.length}
        columns={columns}
      />

      <Modal
        showModal={permissionModal}
        setShowModal={setPermissionModal}
        title={
          userToEdit
            ? `Permissions · ${userToEdit.fullname ?? userToEdit.email}`
            : "Permissions"
        }
        width="w-full md:w-3/5"
      >
        <form onSubmit={updatePermissions} className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            Choose which sections this user can view or manage. Deselecting view
            removes all other actions for that section.
          </p>
          {renderPermissionGrid()}
          <div className="flex items-center justify-end gap-3 pt-4 mt-2 border-t border-gray-200">
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white transition bg-green-600 rounded-md hover:bg-green-500"
            >
              Save changes
            </button>
            <button
              type="button"
              onClick={() => setPermissionModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 transition bg-gray-100 rounded-md hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        width="w-full md:w-2/3"
        showModal={showModal}
        setShowModal={setShowModal}
        title="Add New User"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid items-start gap-4 md:grid-cols-2">
            <input
              type="text"
              placeholder="Full name"
              value={formState.fullname}
              onChange={handleInputChange("fullname")}
              className="w-full px-4 py-2.5 text-sm text-gray-800 border border-gray-300 rounded-md focus:border-gray-400 focus:outline-none focus:ring-0"
            />
            <input
              type="text"
              placeholder="Email"
              value={formState.email}
              onChange={handleInputChange("email")}
              className="w-full px-4 py-2.5 text-sm text-gray-800 border border-gray-300 rounded-md focus:border-gray-400 focus:outline-none focus:ring-0"
            />
            <input
              type="password"
              placeholder="Temporary password"
              value={formState.password}
              autoComplete="new-password"
              onChange={handleInputChange("password")}
              className="w-full px-4 py-2.5 text-sm text-gray-800 border border-gray-300 rounded-md focus:border-gray-400 focus:outline-none focus:ring-0"
            />
            <div>
              <p className="text-sm font-medium text-gray-800">Role</p>
              <div className="flex flex-wrap gap-4 mt-2">
                {roleOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm text-gray-700"
                    htmlFor={`role-${option.value}`}
                  >
                    <Radio
                      name="role"
                      value={option.value}
                      id={`role-${option.value}`}
                      checked={formState.role === option.value}
                      onChange={handleInputChange("role")}
                    />
                    {option.label}
                  </label>
                ))}
                {!roleOptions.length && (
                  <p className="text-xs text-gray-500">
                    You are not allowed to create new users.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-700">
            Set granular permissions by section. Users with no selections will
            only see unprotected pages.
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {renderPermissionGrid()}

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              Passwords must be at least 8 characters and include uppercase,
              lowercase, number, and special character.
            </span>
            <span>{permissionSelection.length} permission rules selected</span>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 mt-2 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white transition bg-green-600 rounded-md disabled:bg-green-300 disabled:cursor-not-allowed hover:bg-green-500"
            >
              {saving ? "Saving..." : "Create user"}
            </button>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 transition bg-gray-100 rounded-md hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </Fragment>
  )
}
